import { useCallback, useEffect, useState } from 'react'

import { ApiError, api, can, refusalReason, type DiscoveryMode, type SuiteLinkSettings } from '../../lib/api'
import { ErrorBox, Loading } from '../../components/DataTable'
import { Status } from '../../components/kit'
import { useToast } from '../../components/ui'

// Settings > Suite links (release 3.1, persona spec 7.4, work package T4).
//
// Where the header's "FOCTTA Consent" and "FOCTTA Discovery" links point. The
// Consent address is the one this workspace is paired with (Settings >
// Platform); an address typed here wins, for an estate whose pairing address
// only the servers can reach. Discovery is reached through Consent (its
// /discovery page signs the person in with the bridge Consent already runs),
// at an address of its own for a Discovery-only estate, or not linked at all.
//
// Only the address is stored. A link never carries a token, a code, an email
// or a role (persona spec P10), so an address with a query string, a
// fragment or a user name is refused by the API and by the database.
//
// Read by whoever may read configuration; changed only with config.write,
// which the IT admin and the TPRM lead hold.

export function SuiteLinksSettings() {
  const [state, setState] = useState<SuiteLinkSettings | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [consentShown, setConsentShown] = useState(true)
  const [consentUrl, setConsentUrl] = useState('')
  const [mode, setMode] = useState<DiscoveryMode>('OFF')
  const [discoveryUrl, setDiscoveryUrl] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<unknown>(null)
  const toast = useToast()
  const mayWrite = can('config.write')

  const load = useCallback(() => {
    api
      .suiteLinkSettings()
      .then((s) => {
        setState(s)
        setConsentShown(s.consentShown)
        setConsentUrl(s.consentUrl)
        setMode(s.discoveryMode)
        setDiscoveryUrl(s.discoveryUrl)
        setError(null)
      })
      .catch(setError)
  }, [])
  useEffect(load, [load])

  async function save() {
    setSaving(true)
    setSaveError(null)
    try {
      const s = await api.saveSuiteLinkSettings({ consentShown, consentUrl, discoveryMode: mode, discoveryUrl })
      setState(s)
      toast('Suite links saved. The header shows them from the next page a person opens.')
    } catch (err) {
      setSaveError(err instanceof ApiError ? err : new ApiError(0, 'CLIENT_ERROR', String(err)))
    } finally {
      setSaving(false)
    }
  }

  if (error != null) return <ErrorBox error={error} what="The suite links" />
  if (state == null) return <Loading what="the suite links" />

  const effectiveConsent = (consentUrl.trim() || state.pairedConsentUrl).replace(/\/+$/, '')
  return (
    <section className="panel" aria-labelledby="suite-links-h">
      <h2 id="suite-links-h">Suite links</h2>
      <p className="muted">
        The header offers these links to {state.shownTo.join(', ')}. Each opens the other product in a new tab; the person signs in
        there as they normally do. Nothing about the person travels in the link.
      </p>

      <h3>FOCTTA Consent</h3>
      <p>
        {state.pairedConsentUrl ? (
          <>
            Paired with <span className="mono">{state.pairedConsentUrl}</span> (Settings &gt; Platform).
          </>
        ) : (
          'Not paired with FOCTTA Consent. Type the address people open Consent at, or pair under Settings > Platform.'
        )}
      </p>
      <label className="check">
        <input
          type="checkbox"
          checked={consentShown}
          disabled={!mayWrite}
          onChange={(e) => setConsentShown(e.target.checked)}
        />{' '}
        Show the FOCTTA Consent link
      </label>
      <label>
        Address people open Consent at{' '}
        <span className="muted">(leave empty to use the paired address)</span>
        <input
          type="url"
          className="mono"
          value={consentUrl}
          placeholder={state.pairedConsentUrl || 'https://consent.example.com'}
          disabled={!mayWrite}
          onChange={(e) => setConsentUrl(e.target.value)}
        />
      </label>

      <h3>FOCTTA Discovery</h3>
      <fieldset disabled={!mayWrite}>
        <legend className="vh">Where the Discovery link goes</legend>
        <label className="check">
          <input type="radio" name="discovery-mode" checked={mode === 'OFF'} onChange={() => setMode('OFF')} /> No Discovery link
        </label>
        <label className="check">
          <input type="radio" name="discovery-mode" checked={mode === 'THROUGH_CONSENT'} onChange={() => setMode('THROUGH_CONSENT')} />{' '}
          Through FOCTTA Consent{effectiveConsent ? ' (' + effectiveConsent + '/discovery)' : ''}
        </label>
        <label className="check">
          <input type="radio" name="discovery-mode" checked={mode === 'DIRECT'} onChange={() => setMode('DIRECT')} /> Straight to a
          Discovery address
        </label>
        {mode === 'DIRECT' && (
          <label>
            Discovery address
            <input
              type="url"
              className="mono"
              value={discoveryUrl}
              placeholder="https://discovery.example.com"
              onChange={(e) => setDiscoveryUrl(e.target.value)}
            />
          </label>
        )}
      </fieldset>

      <p className="muted">
        Now:{' '}
        {state.consentLink ? <Status tone="ok">Consent link on</Status> : <Status tone="plain">No Consent link</Status>}{' '}
        {state.discoveryLink ? <Status tone="ok">Discovery link on</Status> : <Status tone="plain">No Discovery link</Status>}
        {state.updatedBy ? ' Last changed by ' + state.updatedBy + (state.updatedAt ? ' on ' + state.updatedAt.slice(0, 10) : '') + '.' : ''}
      </p>

      {saveError != null && <ErrorBox error={saveError} what="The suite links" verb="saved" />}
      {mayWrite ? (
        <button
          type="button"
          className="button primary"
          disabled={saving}
          onClick={() => void save()}
          data-action="tprm.settings-suite-links.a01"
        >
          {saving ? 'Saving' : 'Save'}
        </button>
      ) : (
        <p className="muted">{refusalReason('config.write')}</p>
      )}
    </section>
  )
}
